import { useEffect, useRef, useCallback } from "react";
import * as API from "../lib/api";
import { toast } from "sonner";
import { recordPollTick, fireNotification } from "../lib/notifications";

/**
 * Reminder polling hook.
 *
 * Simple in-app + SW notification model:
 *   1. Server cron atomically claims due reminders (notified=1) every 30s.
 *   2. While the app is open or recently active, this hook polls every 30s.
 *   3. For every freshly-surfaced reminder (notified=1 within last 5min),
 *      we fire a SW notification AND show a sonner toast in-page.
 *   4. Per-session dedupe so the same reminder isn't notified twice if
 *      polling overlaps the cron window.
 *
 * When the app is fully closed, notifications won't fire — the reminder
 * still gets marked notified=1 server-side and will appear in the list when
 * the user reopens. This is the intentional simplified model (post-V8.6
 * server-push rollback).
 */
export function useReminderPolling(enabled) {
  const seenRef = useRef(new Set());

  const handleFired = useCallback(async (rem) => {
    if (seenRef.current.has(rem.id)) return;
    seenRef.current.add(rem.id);

    // 1) System notification via SW (renders even when tab is unfocused)
    fireNotification(rem.title, {
      body: rem.description || "Reminder due",
      tag: `reminder-${rem.id}`,
      data: { url: "/app/reminders", reminderId: rem.id },
      vibrate: [120, 60, 120],
    }).catch(() => {});

    // 2) In-page toast (in case notification permission isn't granted)
    toast(rem.title, {
      description: rem.description || "Reminder due",
      duration: 8000,
      id: `reminder-${rem.id}`,
    });

    // 3) Tell /reminders page to highlight the row
    try {
      window.dispatchEvent(
        new CustomEvent("journey:reminder-fired", {
          detail: { reminderId: rem.id, title: rem.title },
        })
      );
    } catch {}
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const surfaced = await API.checkDueReminders();
        if (cancelled) return;
        recordPollTick(`ok (${surfaced.length} surfaced)`);
        surfaced.forEach(handleFired);
      } catch (e) {
        recordPollTick(`error: ${e?.message || "unknown"}`);
      }
    };

    tick();
    const id = setInterval(tick, 30000);

    const onFocus = () => tick();
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, handleFired]);
}
